import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Play, Clock } from "lucide-react";

export function TeacherTodaysSessions({ 
  sessions, 
  onStartSession, 
  maxItems = 3, 
  from = "auto",
  batchDetails = {} 
}) {
  const [searchParams] = useSearchParams();
  const [showMoreButton, setShowMoreButton] = useState(sessions?.length > maxItems);
  const [maxItemstate, setMaxItems] = useState(maxItems);
  const givemeLink = searchParams.has("givemelink");

  const displaySessions = maxItemstate ? sessions?.slice(0, maxItemstate) : sessions;

  const handleMore = () => {
    const newMax = maxItemstate + 3;
    setMaxItems(newMax);
    if (newMax >= sessions?.length) setShowMoreButton(false);
  };

  const getSessionStatus = (session) => {
    if (session.postpone_status === 1) return "postponed";

    const now = new Date();
    const sessionStart = new Date(`${session.date}T${session.start_time}`);
    const sessionEnd = new Date(`${session.date}T${session.end_time}`);
    const earlyLiveStart = new Date(sessionStart.getTime() - 10 * 60 * 1000);

    if (now < earlyLiveStart) return "upcoming";
    if (now >= earlyLiveStart && now <= sessionEnd) return "live";
    return "completed";
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="col-12 mb-4">
      <div className="card yellowborder rounded-3">
        <div className="card-header yellow-card-header py-3">
          <h5 className="fw-bold mb-0 newfont d-flex align-items-center gap-2">
            <i className="bi bi-calendar3"></i>
            Today's Sessions
          </h5>
        </div>
        <div className="card-body p-3">
          {!sessions || sessions.length === 0 ? (
            <div className="text-center text-muted py-4">
              <img src="/assets/images/nosession-today.png" alt="No Session" className="img-fluid" style={{ maxHeight: "100px" }} />
              <p className="mb-0 mt-2">There is no session today.</p>
            </div>
          ) : (
            <>
              {displaySessions.map((session) => {
                const status = getSessionStatus(session);
                const batchInfo = batchDetails[session.batch_id] || {};

                return (
                  <div key={session.id} className="row align-items-center mb-3 p-3 border rounded bg-light">
                    <div className="col-md-9">
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                        <span className="fw-bold">
                          {batchInfo.batch_name || `Batch #${session.batch_id}`}
                        </span>
                        {batchInfo.program_title && (
                          <span className="text-muted small">• {batchInfo.program_title}</span>
                        )}
                      </div>
                      <div className="d-flex flex-wrap gap-3 text-muted small">
                        <div className="d-flex align-items-center gap-1">
                          <Clock size={14} />
                          {formatTime(session.start_time)} - {formatTime(session.end_time)}
                        </div>
                        <span className="text-capitalize">{session.day}</span>
                        {status === "live" && <span className="text-danger fw-bold">● Live Now</span>}
                      </div>
                    </div>

                    <div className="col-md-3 d-flex justify-content-end">
                      {givemeLink && session.meet_url && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(session.meet_url);
                            alert("Meeting link copied!");
                          }}
                          className="btn btn-sm btn-outline-success mb-2 w-100"
                        >
                          <i className="ri-file-copy-line me-1"></i> Copy Link
                        </button>
                      )}

                      {status === "live" && session.meet_url && (
                        <button
                          onClick={() => onStartSession?.(session)}
                          className="btn btn-sm btn-danger d-flex align-items-center justify-content-center gap-1 "
                        >
                          <Play size={16} />
                          Join Now
                        </button>
                      )}

                      {status === "upcoming" && (
                        <button className="btn btn-sm btn-primary" disabled>
                          Upcoming
                        </button>
                      )}

                      {status === "completed" && (
                        <button className="btn btn-sm btn-secondary" disabled>
                          Completed
                        </button>
                      )}

                      {status === "postponed" && (
                        <button className="btn btn-sm btn-warning w-100" disabled>
                          Postponed
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {showMoreButton && (
                <div className="text-center mt-3">
                  <button onClick={handleMore} className="btn btn-soft-primary btn-sm">
                    View More <i className="ri-arrow-down-s-line align-middle"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}